import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Package, Boxes, Sparkles, Search } from 'lucide-react';
import { Heading } from '../layout';
import { Dialog, TabNavigation } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import inventoryAPI from '../../services/inventoryAPI';
import { formatInventoryQty } from '../../utils/packFormatQty';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CL');
};

const STATUS_STYLE = (status, isDarkMode) => {
  const s = String(status || '').toLowerCase();
  if (s === 'activo' || s === 'confirmado') {
    return { bg: isDarkMode ? '#14532d' : '#dcfce7', color: isDarkMode ? '#bbf7d0' : '#166534', border: isDarkMode ? '#22c55e' : '#86efac' };
  }
  if (s === 'bajo stock' || s === 'borrador') {
    return { bg: isDarkMode ? '#78350f' : '#ffedd5', color: isDarkMode ? '#fdba74' : '#c2410c', border: isDarkMode ? '#f59e0b' : '#fdba74' };
  }
  if (s === 'sin stock' || s === 'anulado') {
    return { bg: isDarkMode ? '#7f1d1d' : '#fee2e2', color: isDarkMode ? '#fca5a5' : '#b91c1c', border: isDarkMode ? '#ef4444' : '#fecaca' };
  }
  return { bg: isDarkMode ? '#27272a' : '#f0ebe3', color: isDarkMode ? '#e4e4e7' : '#44403c', border: isDarkMode ? '#3f3f46' : '#d6d0c4' };
};

const INVENTORY_TABS = [
  { key: 'raw_materials', label: 'Materias primas', icon: <Package size={16} /> },
  { key: 'supplies', label: 'Insumos', icon: <Boxes size={16} /> },
  { key: 'upselling', label: 'Upselling', icon: <Sparkles size={16} /> },
];

const STOCK_COLUMNS = [
  { key: 'SKU', sortKey: 'sku', align: 'left' },
  { key: 'Nombre', sortKey: 'name', align: 'left' },
  { key: 'Categoría', sortKey: 'category', align: 'left' },
  { key: 'Stock', sortKey: 'stock_qty', align: 'right' },
  { key: 'Mínimo', sortKey: 'min_stock', align: 'right' },
  { key: 'Ubicación', sortKey: 'location', align: 'left' },
  { key: 'Costo', sortKey: 'unit_cost', align: 'right' },
  { key: 'Estado', sortKey: 'status', align: 'center' },
];

const INGRESO_COLUMNS = [
  { key: 'Código', sortKey: 'movement_code', align: 'left' },
  { key: 'Fecha', sortKey: 'movement_date', align: 'left' },
  { key: 'Referencia', sortKey: 'reference', align: 'left' },
  { key: 'SKU', sortKey: 'sku', align: 'left' },
  { key: 'Producto', sortKey: 'name', align: 'left' },
  { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
  { key: 'Origen', sortKey: 'source', align: 'left' },
  { key: 'Estado', sortKey: 'status', align: 'center' },
];

const EGRESO_COLUMNS = [
  { key: 'Código', sortKey: 'movement_code', align: 'left' },
  { key: 'Fecha', sortKey: 'movement_date', align: 'left' },
  { key: 'Referencia', sortKey: 'reference', align: 'left' },
  { key: 'SKU', sortKey: 'sku', align: 'left' },
  { key: 'Producto', sortKey: 'name', align: 'left' },
  { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
  { key: 'Destino', sortKey: 'destination', align: 'left' },
  { key: 'Estado', sortKey: 'status', align: 'center' },
];

const InventoryDataTable = ({
  title,
  rows,
  columns,
  isDarkMode,
  searchPlaceholder,
  defaultSortKey,
  renderCells,
  onRowClick,
  hideChrome = false,
}) => {
  const ui = useUIColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return rows;
    return rows.filter((row) => {
      const hay = Object.values(row)
        .filter((v) => v != null && typeof v !== 'object')
        .join(' ')
        .toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [rows, searchQuery]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const mult = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortColumn];
      const vb = b[sortColumn];
      if (String(sortColumn).includes('date')) {
        return ((va ? new Date(va).getTime() : 0) - (vb ? new Date(vb).getTime() : 0)) * mult;
      }
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return String(va || '').localeCompare(String(vb || ''), 'es', { numeric: true }) * mult;
    });
    return arr;
  }, [filtered, sortColumn, sortDir]);

  const handleSort = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(String(key).includes('date') || typeof rows?.[0]?.[key] === 'number' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={ui.muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={ui.text} /> : <ArrowDown size={12} color={ui.text} />;
  };

  return (
    <div
      style={
        hideChrome
          ? { background: ui.page }
          : {
              border: `1px solid ${ui.border}`,
              borderRadius: 12,
              overflow: 'hidden',
              background: ui.page,
            }
      }
    >
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{title}</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              {sorted.length} de {rows.length} registros
            </p>
          </div>
        </div>
      )}

      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${ui.border}` }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 420 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={ui.muted} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                height: 36,
                padding: '0 10px',
                borderRadius: 8,
                border: `1px solid ${ui.inputBorder}`,
                background: ui.inputBg,
                color: ui.text,
                fontSize: 13,
                outline: 'none',
                flex: 1,
                minWidth: 0,
              }}
            />
          </span>
        </label>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead>
            <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.sortKey)}
                  style={{
                    padding: '8px 10px',
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: ui.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 24, textAlign: 'center', color: ui.muted }}>
                  Sin registros para mostrar.
                </td>
              </tr>
            ) : (
              sorted.map((row) => (
                <tr
                  key={row.id}
                  style={{ borderBottom: `1px solid ${ui.border}`, cursor: onRowClick ? 'pointer' : 'default' }}
                  onClick={() => onRowClick?.(row)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = ui.rowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {renderCells(row, ui)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InventoryTypePanel = ({ inventory, isDarkMode }) => {
  const ui = useUIColors();
  const [viewTab, setViewTab] = useState('stock');
  const [detail, setDetail] = useState(null);
  const [stock, setStock] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [stockRows, ingresoRows, egresoRows] = await Promise.all([
          inventoryAPI.getStock(inventory.key),
          inventoryAPI.getIngresos(inventory.key),
          inventoryAPI.getEgresos(inventory.key),
        ]);
        if (cancelled) return;
        setStock(Array.isArray(stockRows) ? stockRows : []);
        setIngresos(Array.isArray(ingresoRows) ? ingresoRows : []);
        setEgresos(Array.isArray(egresoRows) ? egresoRows : []);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'No se pudo cargar el inventario.');
        setStock([]);
        setIngresos([]);
        setEgresos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [inventory.key]);

  const lowStock = stock.filter((r) =>
    ['bajo stock', 'sin stock'].includes(String(r.status).toLowerCase())
  ).length;

  const innerTabs = [
    { key: 'stock', label: 'Stock', count: stock.length },
    { key: 'ingresos', label: 'Ingresos', count: ingresos.length },
    { key: 'egresos', label: 'Egresos', count: egresos.length },
  ];

  const listSubtitle = loading
    ? 'Cargando…'
    : error || `${stock.length} ítems · ${lowStock} alertas · ${ingresos.length} ingresos · ${egresos.length} egresos`;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}
      >
        {[
          { label: 'Ítems en stock', value: stock.length },
          { label: 'Alertas', value: lowStock },
          { label: 'Ingresos', value: ingresos.length },
          { label: 'Egresos', value: egresos.length },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              border: `1px solid ${ui.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              background: isDarkMode ? '#18181b' : '#fff',
            }}
          >
            <div style={{ fontSize: 11, color: ui.muted }}>{card.label}</div>
            <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, color: ui.text }}>{card.value}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{inventory.label}</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
        </div>

        <div
          role="tablist"
          aria-label={`Vistas de inventario ${inventory.label}`}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {innerTabs.map((tab) => {
            const active = viewTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setViewTab(tab.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{tab.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

      {viewTab === 'stock' ? (
        <InventoryDataTable
          title={`Stock · ${inventory.label}`}
          hideChrome
          rows={stock}
          columns={STOCK_COLUMNS}
          isDarkMode={isDarkMode}
          searchPlaceholder="SKU, nombre, ubicación…"
          defaultSortKey="name"
          onRowClick={setDetail}
          renderCells={(row, colors) => {
            const st = STATUS_STYLE(row.status, isDarkMode);
            return (
              <>
                <td style={{ padding: '9px 10px', fontWeight: 700, fontSize: 12, color: colors.text }}>{row.sku}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.text }}>{row.name}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.category}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>
                  {formatInventoryQty(row.stock_qty, row.unit, row.format_code)}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', color: colors.muted }}>
                  {row.min_stock}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.location}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>
                  {formatMoney(row.unit_cost)}
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                    }}
                  >
                    {row.status}
                  </span>
                </td>
              </>
            );
          }}
        />
      ) : null}

      {viewTab === 'ingresos' ? (
        <InventoryDataTable
          title={`Ingresos · ${inventory.label}`}
          hideChrome
          rows={ingresos}
          columns={INGRESO_COLUMNS}
          isDarkMode={isDarkMode}
          searchPlaceholder="Código, SKU, referencia, origen…"
          defaultSortKey="movement_date"
          onRowClick={setDetail}
          renderCells={(row, colors) => {
            const st = STATUS_STYLE(row.status, isDarkMode);
            return (
              <>
                <td style={{ padding: '9px 10px', fontWeight: 700, fontSize: 12, color: colors.text }}>
                  {row.movement_code}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>
                  {formatDate(row.movement_date)}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.text }}>{row.reference}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.sku}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.text }}>{row.name}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>
                  {formatInventoryQty(row.quantity, row.unit, row.format_code)}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.source}</td>
                <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                    }}
                  >
                    {row.status}
                  </span>
                </td>
              </>
            );
          }}
        />
      ) : null}

      {viewTab === 'egresos' ? (
        <InventoryDataTable
          title={`Egresos · ${inventory.label}`}
          hideChrome
          rows={egresos}
          columns={EGRESO_COLUMNS}
          isDarkMode={isDarkMode}
          searchPlaceholder="Código, SKU, referencia, destino…"
          defaultSortKey="movement_date"
          onRowClick={setDetail}
          renderCells={(row, colors) => {
            const st = STATUS_STYLE(row.status, isDarkMode);
            return (
              <>
                <td style={{ padding: '9px 10px', fontWeight: 700, fontSize: 12, color: colors.text }}>
                  {row.movement_code}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>
                  {formatDate(row.movement_date)}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.text }}>{row.reference}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.sku}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.text }}>{row.name}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>
                  {formatInventoryQty(row.quantity, row.unit, row.format_code)}
                </td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: colors.muted }}>{row.destination}</td>
                <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                    }}
                  >
                    {row.status}
                  </span>
                </td>
              </>
            );
          }}
        />
      ) : null}
      </div>

      <Dialog
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail?.movement_code ? 'Movimiento' : 'Ítem de stock'}
        subtitle={detail?.sku || detail?.movement_code || ''}
        isDarkMode={isDarkMode}
        size="md"
      >
        {detail ? (
          <div style={{ padding: '0 20px 20px', display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: ui.text }}>{detail.name}</div>
            {Object.entries(detail)
              .filter(([k]) => !['id'].includes(k))
              .map(([key, value]) => (
                <div key={key}>
                  <div style={{ fontSize: 11, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {key}
                  </div>
                  <div style={{ fontSize: 14, color: ui.text, marginTop: 2 }}>
                    {key.includes('date')
                      ? formatDate(value)
                      : key.includes('cost')
                        ? formatMoney(value)
                        : String(value ?? '—')}
                  </div>
                </div>
              ))}
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

const PaintProcurementInventoryWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();

  const tabs = INVENTORY_TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
    content: <InventoryTypePanel inventory={tab} isDarkMode={isDarkMode} />,
  }));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Inventarios
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Tres inventarios de adquisiciones: materias primas, insumos y upselling. El ingreso se registra al marcar
          Recepción Conforme en la OC. Servicios y transporte no mueven stock.
        </p>
      </div>

      <TabNavigation tabs={tabs} defaultTab="raw_materials" isDarkMode={isDarkMode} />
    </div>
  );
};

export default PaintProcurementInventoryWorkspace;
